import qs from 'qs';

// This is a JS file instead of TS because it is imported by a PetSafe JS file,
// moving to TS would require refactoring a large amount of JS files to TS.
// Keep this file TS-free for sites/petsafe/src/node/collections.js.

/** @typedef {'petsafe' | 'ifb' | 'sportdog' | 'kurgo' | 'storybook'} AlgoliaSiteIdentifier */

/**
 * @typedef {object} AlgoliaCollectionSearchConfig
 * @property {string} productIndexName
 * @property {string} filters
 * @property {string} hierarchyAttribute
 * @property {string} defaultHierarchicalCategory
 * @property {import('instantsearch.js').UiState} initialUiState
 */

/** Needs to be ' > ' as this is the separator used in the Algolia index data. Must match ALGOLIA_DATA_HIERARCHICAL_PATH_SEPARATOR in algoliaConstants.ts */
const ALGOLIA_DATA_HIERARCHICAL_PATH_SEPARATOR = ' > ';
export const COLLECTION_HIERARCHY_ATTRIBUTE = 'hierarchicalCategories.lvl0';
const isProductionEnvironment =
  process.env.NODE_ENV === 'production' && process.env.CONTEXT === 'production';

/**
 * @param {AlgoliaSiteIdentifier} siteIdentifier
 * @param {string} locale
 * @returns {string}
 */
export function getAlgoliaProductIndexName(siteIdentifier, locale) {
  let brandPrefix = '';
  const skipBrandPrefix =
    siteIdentifier === 'petsafe' ||
    (isProductionEnvironment && siteIdentifier === 'sportdog');
  if (!skipBrandPrefix) brandPrefix = `${siteIdentifier}_`;

  if (siteIdentifier === 'ifb') return `${brandPrefix}products_all`;

  // Support Sites don't have an Algolia product index, so return an empty string to avoid Algolia search attempts
  if (['sportdog-support', 'petsafe-support'].includes(siteIdentifier))
    return '';

  return `${brandPrefix}globalProducts_${locale}`;
}

/**
 * @param {AlgoliaSiteIdentifier} siteIdentifier
 * @param {string} locale
 * @returns {string}
 */
export function getAlgoliaBlogIndexName(siteIdentifier, locale) {
  let brandPrefix = '';
  const skipBrandPrefix =
    siteIdentifier === 'petsafe' ||
    (isProductionEnvironment && siteIdentifier === 'sportdog');
  if (!skipBrandPrefix) brandPrefix = `${siteIdentifier}_`;

  return `${brandPrefix}blogPosts_${locale}`;
}

/**
 * @param {AlgoliaSiteIdentifier} siteIdentifier
 * @param {string} fallbackLocale
 * @param {string} currency
 * @returns {string}
 */
export function getIfbLocaleByCurrency(
  siteIdentifier,
  fallbackLocale,
  currency
) {
  if (siteIdentifier !== 'ifb' || currency !== 'CAD') return fallbackLocale;
  return 'en-ca';
}

/**
 * @param {AlgoliaSiteIdentifier} siteIdentifier
 * @param {string} locale
 * @returns {string}
 */
export function getAlgoliaPageIndexName(siteIdentifier, locale) {
  let brandPrefix = '';

  if (siteIdentifier !== 'petsafe') {
    brandPrefix = `${siteIdentifier}_`;
  }

  return `${brandPrefix}pages_${locale}`;
}

/**
 * @returns {string}
 */
export function getAlgoliaProductsFilter() {
  return 'NOT canBeShown:false';
}

/**
 * @param {string | null | undefined} shopifyCollectionTitle
 * @returns {string}
 */
export function getDefaultHierarchicalCategory(shopifyCollectionTitle) {
  const cleanPath = shopifyCollectionTitle
    ?.replace(/^\/shop\//, '')
    .replace(/\/$/, '');
  const segments = cleanPath ? cleanPath.split('/') : [];

  return segments.join(ALGOLIA_DATA_HIERARCHICAL_PATH_SEPARATOR);
}

/**
 * JS-only collection config does not depend on the TS clean URL mapping layer.
 * The current JS consumer is build-time collection metadata generation, which
 * only needs the path-derived hierarchical state.
 *
 * @param {string | null | undefined} pathname
 * @param {string} indexName
 * @param {string} hierarchyAttribute
 * @param {string} [search]
 * @returns {import('instantsearch.js').UiState}
 */
export function generateInitialStateFromUrl(
  pathname,
  indexName,
  hierarchyAttribute,
  search
) {
  const cleanPath = pathname?.replace(/^\/shop\//, '').replace(/\/$/, '');
  const segments = cleanPath
    ? cleanPath.split('/').map(segment => decodeURIComponent(segment))
    : [];
  const hierarchicalPath = segments.join(
    ALGOLIA_DATA_HIERARCHICAL_PATH_SEPARATOR
  );
  const hierarchicalMenu = hierarchicalPath
    ? { [hierarchyAttribute]: [hierarchicalPath] }
    : {};
  const urlParams = search ? qs.parse(search.slice(1)) : {};
  const indexParams = urlParams[indexName];
  const urlHierarchicalMenu = indexParams?.hierarchicalMenu || {};

  return {
    [indexName]: {
      ...indexParams,
      hierarchicalMenu: {
        ...hierarchicalMenu,
        ...urlHierarchicalMenu,
      },
    },
  };
}

/**
 * @param {object} options
 * @param {AlgoliaSiteIdentifier} options.siteIdentifier
 * @param {string} options.locale
 * @param {string} options.shopifyCollectionTitle
 * @param {string | null} [options.currency]
 * @param {string} [options.search]
 * @param {boolean} [options.includeSearch]
 * @returns {AlgoliaCollectionSearchConfig}
 */
export function getAlgoliaCollectionSearchConfig(options) {
  const {
    siteIdentifier,
    locale,
    shopifyCollectionTitle,
    currency,
    search,
    includeSearch = false,
  } = options;

  const resolvedLocale = getIfbLocaleByCurrency(
    siteIdentifier,
    locale,
    currency ?? ''
  );
  const productIndexName = getAlgoliaProductIndexName(
    siteIdentifier,
    resolvedLocale
  );
  const hierarchyAttribute = COLLECTION_HIERARCHY_ATTRIBUTE;
  const defaultHierarchicalCategory = getDefaultHierarchicalCategory(
    shopifyCollectionTitle
  );

  return {
    productIndexName,
    filters: getAlgoliaProductsFilter(),
    hierarchyAttribute,
    defaultHierarchicalCategory,
    initialUiState: generateInitialStateFromUrl(
      shopifyCollectionTitle,
      productIndexName,
      hierarchyAttribute,
      includeSearch ? search : undefined
    ),
  };
}
